import { useState } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Calendar, Clock, User, Phone, Mail, MapPin } from 'lucide-react'

import Section from '../components/ui/Section'
import GlassCard from '../components/ui/GlassCard'
import Input from '../components/ui/Input'
import Textarea from '../components/ui/Textarea'
import Button from '../components/ui/Button'

const Appointment = () => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    date: '',
    time: '',
    message: '',
  })

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000))
    
    console.log('Appointment request:', formData)
    setLoading(false)
    
    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      service: '',
      date: '',
      time: '',
      message: '',
    })
    
    alert('Demande de rendez-vous envoyée avec succès !')
  }

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const services = [
    'Plan 1 - Occupation de Courte durée',
    'Plan 2 - Occupation par alternance', 
    'Plan 3 - Occupation de Long Durée',
    'Visite de résidence',
    'Consultation personnalisée',
    'Autre'
  ]

  return (
    <div className="min-h-screen pt-32 pb-20">
      <Section
        title="Prendre un Rendez-vous"
        subtitle="Planifiez votre visite ou consultation"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <GlassCard className="p-8">
              <h3 className="text-2xl font-bold gradient-text mb-6">
                Demande de Rendez-vous
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <Input
                  label="Nom complet"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  icon={User}
                  placeholder="Jean Dupont"
                  required
                />

                <Input
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  icon={Mail}
                  placeholder="jean@example.com"
                  required
                />

                <Input
                  label="Téléphone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  icon={Phone}
                  placeholder="(450) 123-4567"
                  required
                />

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-black">
                    Service demandé
                  </label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className="glass-input w-full"
                    required
                  >
                    <option value="">Sélectionnez un service</option>
                    {services.map((service) => (
                      <option key={service} value={service}>
                        {service}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Date souhaitée"
                    name="date"
                    type="date"
                    value={formData.date}
                    onChange={handleChange}
                    icon={Calendar}
                    required
                  />

                  <Input
                    label="Heure souhaitée"
                    name="time"
                    type="time"
                    value={formData.time}
                    onChange={handleChange}
                    icon={Clock}
                    required
                  />
                </div>

                <Textarea
                  label="Message (optionnel)"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Informations supplémentaires ou questions..."
                />

                <Button
                  type="submit"
                  variant="primary"
                  className="w-full"
                  loading={loading}
                >
                  {loading ? 'Envoi en cours...' : 'Demander un rendez-vous'}
                </Button>
              </form>
            </GlassCard>
          </motion.div>

          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-6"
          >
            <GlassCard className="p-8">
              <h3 className="text-2xl font-bold gradient-text mb-6">
                Informations de Contact
              </h3>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="glass-card p-3 bg-primary-500">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-black mb-1">
                      Adresse
                    </h4>
                    <p className="text-gray-600">
                      444 Avenue Yvon-Berger<br />
                      Laval, Québec H7L 2C2, Canada
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <div className="glass-card p-3 bg-primary-500">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-black mb-1">
                      Téléphone
                    </h4>
                    <a href="tel:+14506282696" className="text-gray-600 hover:text-primary-600">
                      (450) 628-2696
                    </a>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <div className="glass-card p-3 bg-primary-500">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-black mb-1">
                      Email
                    </h4>
                    <a href="mailto:admin@residencemassena.com" className="text-gray-600 hover:text-primary-600">
                      admin@residencemassena.com
                    </a>
                  </div>
                </div>
              </div>
            </GlassCard>

            <GlassCard className="p-8">
              <h3 className="text-xl font-bold gradient-text mb-4">
                Horaires d'Ouverture
              </h3>
              <div className="space-y-2 text-gray-600">
                <p><strong>Lundi - Vendredi :</strong> 8h - 17h</p>
                <p><strong>Samedi :</strong> 9h - 15h</p>
                <p><strong>Dimanche :</strong> Fermé</p>
              </div>
            </GlassCard>

            <GlassCard className="p-8">
              <h3 className="text-xl font-bold gradient-text mb-4">
                Processus de Rendez-vous
              </h3>
              <div className="space-y-3 text-gray-600">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-primary-500 text-white rounded-full flex items-center justify-center text-sm font-bold mt-0.5">
                    1
                  </div>
                  <p>Envoyez votre demande via ce formulaire</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-primary-500 text-white rounded-full flex items-center justify-center text-sm font-bold mt-0.5">
                    2
                  </div>
                  <p>Nous vous contacterons dans les 24h</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-primary-500 text-white rounded-full flex items-center justify-center text-sm font-bold mt-0.5">
                    3
                  </div>
                  <p>Planification de la visite ou consultation</p>
                </div>
              </div>
            </GlassCard>
          </motion.div>
        </div>
      </Section>
    </div>
  )
}

export default Appointment
